// @flow

import styles from './JobDescription.less';

import React, { Component } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash';

import {
  ScreenReaderOnly,
  Card,
  CardGroup,
  Section,
  Hidden
} from 'seek-style-guide/react';
import JobMeta from '../JobMeta/JobMeta';
import ReportThisAd from '../ReportThisAd/ReportThisAd';
import RightToWork from '../RightToWork/RightToWork';
import ShareButtons from '../ShareButtons/ShareButtons';
import JobHeader from '../JobHeader/JobHeader';
import TopApplicantBadge from '../TopApplicantBadge/TopApplicantBadge';
import ExpandDown from 'src/modules/ExpandDown';

import {
  templateRenderer,
  setTemplateClasses,
  toCISImpression,
  iterateOverContactMatches
} from '@seek/candidate-interest-signals';

import type { RenderTemplateFn } from '@seek/candidate-interest-signals';

import {
  trackJobDetailsPhoneClicked,
  trackJobDetailsEmailClicked,
  trackJobDetailsUrlClicked,
  forceTrackingRequest
} from 'src/modules/job-tracking';

import { cisInteraction, cisImpression } from 'src/store/jobdetails/jobdetails';

type JobDescriptionProps = {
  job: ChaliceJobDetail,
  location: UrlLocation,
  cisInteraction: Function,
  cisImpression: Function,
  seekerId?: number,
  sessionId?: string,
  cisClickToReveal: boolean,
  showTopApplicantBadge: boolean
};

type TemplateStore = {
  Mobile: ?HTMLDivElement,
  Desktop: ?HTMLDivElement
};

type TemplateType = 'Mobile' | 'Desktop';

type ListenerEventType = 'add' | 'remove';

const trackingFunctionsByContactType = {
  Phone: trackJobDetailsPhoneClicked,
  Email: trackJobDetailsEmailClicked,
  Web: trackJobDetailsUrlClicked
};

setTemplateClasses({
  Phone: styles.phoneCIS,
  Email: styles.emailCIS
});

const mapStateToProps = ({ featureFlags, fitme }) => ({
  cisClickToReveal: featureFlags.cisClickToReveal,
  showTopApplicantBadge: fitme.showTopApplicantBadge
});

const mapDispatchToProps = {
  cisInteraction,
  cisImpression
};

class JobDescription extends Component<JobDescriptionProps> {
  constructor(props: JobDescriptionProps) {
    super(props);
    this.renderTemplate = templateRenderer({
      obscureContactMatches: props.cisClickToReveal
    });
    this.template = {
      Mobile: null,
      Desktop: null
    };
  }

  componentDidMount = () => this.attachOrRemoveDOmEvents('add');

  componentDidUpdate(prevProps) {
    const { job: oldJob } = prevProps;
    const { job: newJob, cisImpression: _cisImpression } = this.props;

    if (
      newJob.mobileAdTemplate !== oldJob.mobileAdTemplate ||
      newJob.desktopAdTemplate !== oldJob.desktopAdTemplate
    ) {
      this.attachOrRemoveDOmEvents('add');
      _cisImpression(toCISImpression(newJob));
    }
  }

  componentWillUnmount = () => this.attachOrRemoveDOmEvents('remove');

  template: TemplateStore;
  renderTemplate: RenderTemplateFn;

  listenerReference = (type: ContactTypes, element: HTMLElement) => (
    eventType: ContactInteractionType
  ) => () => {
    const {
      seekerId,
      sessionId,
      job,
      cisInteraction: dispatchCISInteraction
    } = this.props;
    const contact = element.getAttribute('href');

    const impressionTrackingArgs = {
      seekerId,
      sessionId,
      jobId: job.id,
      contact,
      clickType: 'contact'
    };

    dispatchCISInteraction({ type, eventType });
    trackingFunctionsByContactType[type](impressionTrackingArgs);

    if (eventType === 'click') {
      forceTrackingRequest();
    }
  };

  addEventListeners(element: HTMLElement, listener: Function) {
    element.addEventListener('click', listener('click'));
    element.addEventListener('copy', listener('copy'));
  }

  removeEventListeners(element: HTMLElement, listener: Function) {
    element.removeEventListener('click', listener('click'));
    element.removeEventListener('copy', listener('copy'));
  }

  attachOrRemoveDOmEvents = (eventType: ListenerEventType) => {
    ['Mobile', 'Desktop'].forEach(type => {
      const template = this.template[type];

      if (template) {
        this.generateTrackingListener(template, eventType);
      }
    });
  };

  generateTrackingListener = (
    DOMElement: HTMLDivElement,
    eventType: ListenerEventType
  ) => {
    const attachListeners = (type, element) => {
      const listener = this.listenerReference(type, element);

      return eventType === 'add'
        ? this.addEventListeners(element, listener)
        : this.removeEventListeners(element, listener);
    };

    iterateOverContactMatches({ callback: attachListeners, DOMElement });
  };

  setTemplate = (type: TemplateType) => (ref: ?HTMLDivElement) => {
    this.template[type] = ref;
  };

  renderVideoIframe = ({ video, isBelow }) => (
    <iframe
      width="100%"
      height="312"
      src={video.link}
      frameBorder="0"
      allowFullScreen
      className={classnames({
        [styles.video]: true,
        [styles.video_isAbove]: !isBelow,
        [styles.video_isBelow]: isBelow
      })}
    />
  );

  renderVideo = (video: Video, isBelow: boolean) => {
    return (
      <Hidden
        print
        component={this.renderVideoIframe}
        video={video}
        isBelow={isBelow}
      />
    );
  };

  renderMobileTemplate({
    mobileAdTemplate,
    video,
    desktopAdTemplate,
    contactMatches
  }: {
    mobileAdTemplate: string,
    video?: Video,
    desktopAdTemplate?: string,
    contactMatches: Array<ContactMatch>
  }) {
    const { position } = video || {};
    const isBelow = /below/i.test(position);
    const hiddenProps = desktopAdTemplate
      ? {
          desktop: true,
          print: true
        }
      : {};

    return (
      /* eslint-disable react/no-danger */
      <Hidden {...hiddenProps}>
        <div>
          {video && !isBelow && this.renderVideo(video, isBelow)}
          <div
            data-automation="mobileTemplate"
            ref={this.setTemplate('Mobile')}
            className={styles.mobileTemplate}
            dangerouslySetInnerHTML={{
              __html: this.renderTemplate(mobileAdTemplate, contactMatches)
            }}
          />
          {video && isBelow && this.renderVideo(video, isBelow)}
        </div>
      </Hidden>
      /* eslint-enable react/no-danger */
    );
  }

  renderDesktopTemplate({
    desktopAdTemplate,
    contactMatches
  }: {
    desktopAdTemplate?: string,
    contactMatches: Array<ContactMatch>
  }) {
    return (
      /* eslint-disable react/no-danger */
      <Hidden mobile>
        <div
          data-automation="desktopTemplate"
          ref={this.setTemplate('Desktop')}
          dangerouslySetInnerHTML={{
            __html: this.renderTemplate(desktopAdTemplate, contactMatches)
          }}
          className={styles.desktopContainer}
        />
      </Hidden>
      /* eslint-enable react/no-danger */
    );
  }

  renderPlaceholder() {
    return (
      <Section>
        <div className={styles.placeholder} />
      </Section>
    );
  }

  render() {
    const { job, location, showTopApplicantBadge } = this.props;
    const {
      isRightToWorkRequired = true,
      mobileAdTemplate,
      desktopAdTemplate,
      branding
    } = job;

    const logo = get(branding, 'logo.url') || get(branding, 'logo');

    return (
      <article role="region" aria-labelledby="jobDescription">
        <ScreenReaderOnly>
          <h1 id="jobDescription">Job Description</h1>
        </ScreenReaderOnly>
        <CardGroup className={styles.contentWrapper}>
          {logo && (
            <div
              data-automation="brandingLogo"
              className={styles.logoImage}
              style={{
                backgroundImage: `url(${logo})`
              }}
            />
          )}
          <Card className={classnames({ [styles.jobHeader_isBranded]: logo })}>
            <JobHeader {...job} />
          </Card>
          <Card>
            <Hidden component={Section} desktop>
              <JobMeta job={job} />
            </Hidden>
          </Card>
          {
            <ExpandDown in={showTopApplicantBadge}>
              <TopApplicantBadge />
            </ExpandDown>
          }
          <Card>
            {desktopAdTemplate || mobileAdTemplate ? (
              <Section data-automation="jobDescription">
                {desktopAdTemplate && this.renderDesktopTemplate(job)}
                {mobileAdTemplate && this.renderMobileTemplate(job)}
              </Section>
            ) : (
              this.renderPlaceholder()
            )}
          </Card>
          <Card>
            <Hidden component={Section} print>
              <RightToWork required={isRightToWorkRequired} />
              <ReportThisAd />
            </Hidden>
          </Card>
          <ShareButtons location={location} />
        </CardGroup>
      </article>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(JobDescription);
